import { graphql } from 'react-relay';
import { useQuery } from '@atlassian/jira-relay-apollo-migration-utils';
import type {
	deploymentFrequencyCoreRelayQuery$variables,
	deploymentFrequencyCoreRelayQuery$data,
} from '@atlassian/jira-relay/src/__generated__/deploymentFrequencyCoreRelayQuery.graphql';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type { DeploymentFrequencyReportWithProviderInfo } from '../../common/types';
import { calculateDateRangeFor12Weeks, getCurrentDate } from '../../common/utils/date-range';
import { useDeploymentProviders } from '../deployment-provider';
import { useProjectId } from '../get-project';
import { buildQueryVariables, mergeResult, transformResult } from './utils';

export const useReportCoreData =
	(): ServiceQueryResultWithFetch<DeploymentFrequencyReportWithProviderInfo> => {
		const cloudId = useCloudId();
		const projectId = useProjectId();
		const deploymentProvidersResult = useDeploymentProviders();
		const variables = buildQueryVariables(
			cloudId,
			projectId,
			calculateDateRangeFor12Weeks(getCurrentDate()),
		);

		const queryResult = useQuery<
			deploymentFrequencyCoreRelayQuery$variables,
			deploymentFrequencyCoreRelayQuery$data
		>(
			// disabling eslint rule since transformation is happaning inside transformResult
			/* eslint-disable relay/unused-fields */
			graphql`
				query deploymentFrequencyCoreRelayQuery(
					$filter: DevOpsMetricsFilterInput!
					$deploymentFrequencyRollupType: DevOpsMetricsRollupType!
					$deploymentSizeRollupType: DevOpsMetricsRollupType!
				) {
					devOpsMetrics @required(action: THROW) {
						deploymentFrequencyDev: deploymentFrequency(
							filter: $filter
							environmentCategory: DEVELOPMENT
							rollupType: $deploymentFrequencyRollupType
						) @required(action: THROW) {
							data {
								dateTime
								count
							}
						}
						deploymentFrequencyTesting: deploymentFrequency(
							filter: $filter
							environmentCategory: TESTING
							rollupType: $deploymentFrequencyRollupType
						) @required(action: THROW) {
							data {
								dateTime
								count
							}
						}
						deploymentFrequencyStg: deploymentFrequency(
							filter: $filter
							environmentCategory: STAGING
							rollupType: $deploymentFrequencyRollupType
						) @required(action: THROW) {
							data {
								dateTime
								count
							}
						}
						deploymentFrequencyProd: deploymentFrequency(
							filter: $filter
							environmentCategory: PRODUCTION
							rollupType: $deploymentFrequencyRollupType
						) @required(action: THROW) {
							data {
								dateTime
								count
							}
							aggregateData
						}
						deploymentFrequencyUnmapped: deploymentFrequency(
							filter: $filter
							environmentCategory: UNMAPPED
							rollupType: $deploymentFrequencyRollupType
						) @required(action: THROW) {
							data {
								dateTime
								count
							}
						}
						deploymentSizeProd: deploymentSize(
							filter: $filter
							environmentCategory: PRODUCTION
							rollupType: $deploymentSizeRollupType
						) @required(action: THROW) {
							data {
								dateTime
								deploymentSize
							}
							aggregateData
						}
					}
				}
			`,
			/* eslint-enable relay/unused-fields */
			{
				variables: { ...variables },
				// @ts-expect-error - TS2345 - Argument of type '{ variables: { deploymentFrequencyRollupType: DevOpsMetricsRollupType; deploymentSizeRollupType: DevOpsMetricsRollupType; filter: DevOpsMetricsFilterInput; }; errorPolicy: string; }' is not assignable to parameter of type '{ variables?: deploymentFrequencyCoreRelayQuery$variables | undefined; skip?: boolean | undefined; fetchPolicy?: FetchPolicy | undefined; }'.
				errorPolicy: 'all',
			},
		);

		return mergeResult(transformResult(queryResult), deploymentProvidersResult);
	};
